import React, {useEffect, useRef, useState, FC} from "react";
import {filter as filterrx}                     from "rxjs/operators";
import {docKeyDown$}                            from "@sirius/ui-lib/src/subjects/DocKeyDown";
import {HBox, MGap}                             from "@sirius/ui-lib/src/blocks/Layout";
import {Icon}                                   from "@sirius/ui-lib/src/blocks/Controls";
import {Row}                                    from "./Row";
import { getCellStyle }                         from "./getCellStyle";
import {block}                                  from "./block";
import {
    TBodyProps,
    ColOffsetList,
    HasColumn,
    ColCellType,
    HasLastSticky,
    HasSortFilterPopup
}                                               from "./@types";



const getColCellClasses = ({column: {name, sticky, sort, filter}, lastSticky, hasClick, sortFilterPopup}: HasColumn & HasLastSticky & { hasClick: boolean } & HasSortFilterPopup ) => (
    block.el('tbody-cols-cell') +
    block.el('tbody-cols-cell').mod(`name-${name}`                   ) +
    block.el('tbody-cols-cell').bod('sticky',           !!sticky     ) +
    block.el('tbody-cols-cell').bod('sticky-last',      lastSticky   ) +
    block.el('tbody-cols-cell').bod('has-click',        hasClick     ) +
    block.el('tbody-cols-cell').bod(`has-sort-${sort}`, !!sort       ) +
    block.el('tbody-cols-cell').bod('has-filter',       !!filter     ) +
    block.el('tbody-cols-cell').bod('has-sort-filter-popup', sortFilterPopup !== null && sortFilterPopup.name === name )
)



const ColCell:FC<ColCellType> = ({
    ColumnCell ,
    column,
    offset,
    width,
    allChecked,
    toggleAllChecked,
    lastSticky,
    setSortFilterPopup,
    onMouseLeave,
    onMouseOver,
    sortFilterPopup,
    hardWidthDisabled
}) => {
    const cellRef = useRef<HTMLTableCellElement>(null);
    const  { name, sort, filter } = column;
    const hasClick = sort !== void(0) || filter !== void(0);
    const onCellClick = hasClick
        ? () => {
            setTimeout(
                () => {
                    setSortFilterPopup(
                        sortFilterPopup === null || sortFilterPopup?.name !== name
                            ? {name, offset, width}
                            : null
                    )
                }
            )
        }
        : void(0)
    ;
    const cellClasses = getColCellClasses({column, lastSticky, hasClick, sortFilterPopup});
    const cellStyle   = getCellStyle(column, offset, width, hardWidthDisabled);
    const [focus, setFocus] = useState<boolean>(false);
    const KeyboardFilterEnterSpace = filterrx( ({code, target}: KeyboardEvent)=> ((code === 'Enter' /*|| code === 'Space'*/) && cellRef?.current === target) )
    const keyDownHandler = (e: KeyboardEvent) => {
        e.preventDefault();
        onCellClick && focus && onCellClick();
    };
    useEffect(() => {
            const subDKD = docKeyDown$.pipe( KeyboardFilterEnterSpace )
                .subscribe( keyDownHandler )
            ;
            return () => subDKD.unsubscribe()
        },
        [focus]
    );

    const onFocus = () => {  setFocus(true);  }
    const onBlur  = () => {  setFocus(false); }

    return  <th
                onClick   = {onCellClick}
                className = {cellClasses}
                style     = {cellStyle}
                ref       = {cellRef}
            >
                <HBox
                    className = {
                        block.el('tbody-cell-wrapper') +
                        block.el('tbody-cols-cell-wrapper')
                    }
                    data-filter  = {filter}
                    data-sort    = {sort}
                    tabIndex     = {0}
                    onMouseLeave = { onMouseLeave }
                    onMouseOver  = { onMouseOver  }
                    onFocus      = { onFocus      }
                    onBlur       = { onBlur       }
                >
                    <ColumnCell
                        toggleAllChecked={toggleAllChecked}
                        allChecked={allChecked}
                        column={column}
                    />
                    {
                        !!sort &&
                        <>
                            <MGap/>
                            <Icon
                                size={'xs'}
                                icon={  sort === 'asc' ? 'north' : 'south' }
                                className = {block.el('tbody-cols-cell-wrapper-sort-dir')}
                            />
                        </>
                    }
                </HBox>
            </th>
};

const extrOffsetWidth = ({offsetWidth}: HTMLTableCellElement) => offsetWidth;
const sumReducer = (s:number, w:number):number => (s+w) ;
const calcOffset = (widths:number[], i:number): number =>
    widths
        .slice(0, i)
        .reduce(sumReducer,0)
;

const getColWidth = (nextCol: number, currentCol: number, row: number) => (!nextCol ? row : nextCol) - currentCol;

export const TBody: React.FC<TBodyProps> = ({
    checkedRows,
    cols,
    rows,
    width,
    setCheckedRows,
    setSortFilterPopup,
    sortFilterPopup,
    setSortFilterClearPopup,
    RowEditElement,
    hardWidthDisabled,
    RowClassByRow,
    SeparatorElement,
    wrapperRef,
    closeEditOnScroll
}) => {
    const [colOffset, setColOffset] = useState<ColOffsetList>([]);

    const colsRow        = useRef();
    const resizeObserver = useRef<ResizeObserver | null>(null);

    useEffect(
        () => {
            const {cells} = colsRow.current as HTMLTableRowElement;
            const cellsList = [...cells as unknown as HTMLTableCellElement[]];

            const hasTableCells = !!cellsList?.length;

            const updateColOffset = () => {
                if (hasTableCells) {
                    const nextColOffset = cellsList
                        .map(extrOffsetWidth)
                        .reduce(
                            (offsets, w, wi, src) => {
                                offsets[wi] = calcOffset(src, wi);
                                return offsets;
                            },
                            [] as number[]
                        );
                    setColOffset(nextColOffset);
                }
            };

            if (!resizeObserver.current) {
                resizeObserver.current = new ResizeObserver(updateColOffset);
            }

            if (hasTableCells) {
                cellsList.forEach(cell => resizeObserver.current?.observe(cell));
            }

            return () => {
                if (hasTableCells) {
                    cellsList.forEach(cell => resizeObserver.current?.unobserve(cell));
                }
            };
        },
        [width, rows, cols]
    );

    const allChecked = checkedRows.length === rows.length && rows.length > 0;
    const toggleAllChecked = ()    => { setCheckedRows( allChecked ? [] : rows ) }
    const toggleRowChecked = (row: any) => {
        setCheckedRows(
            checkedRows.some( r => JSON.stringify(r) === JSON.stringify(row) )
                ? checkedRows.filter( r => JSON.stringify(r) !== JSON.stringify(row) )
                : [...checkedRows, row]
        )
    }

    const colHoverHandler = (ci: number, offset: number, show: boolean) => (e: React.MouseEvent)=>{
        e.stopPropagation();
        return setSortFilterClearPopup(ci, offset, show)(e);
    }

    return  <tbody className={block.el('tbody')}>
                <tr className={block.el('tbody-cols')} ref={colsRow}>
                    {
                        cols.map(
                            (col, ci) =>
                                <ColCell
                                    key={ci}
                                    {...col}
                                    offset             = {colOffset[ci]}
                                    width              = {getColWidth(colOffset[ci + 1], colOffset[ci], (colsRow?.current as HTMLTableRowElement)?.offsetWidth)}
                                    allChecked         = {allChecked}
                                    lastSticky         = {col.column.sticky && !cols[ci + 1]?.column.sticky}
                                    toggleAllChecked   = {toggleAllChecked}
                                    setSortFilterPopup = {setSortFilterPopup}
                                    sortFilterPopup    = {sortFilterPopup}
                                    hardWidthDisabled  = {hardWidthDisabled}
                                    onMouseLeave       = {colHoverHandler(ci, colOffset[ci], false)}
                                    onMouseOver        = {colHoverHandler(ci, colOffset[ci], true)}
                                />
                        )
                    }
                </tr>
                <tr className={block.el('tbody-cols-shadow')}>
                    <td className={block.el('tbody-cols-shadow-cell')} colSpan={cols.length}>
                        <div className={block.el('tbody-cols-shadow-cell-bg-wrapper')}>
                            <div className={block.el('tbody-cols-shadow-cell-bg')}/>
                        </div>
                    </td>
                </tr>
                {
                    rows.length > 0 &&
                    rows.map(
                        (row, ri) =>
                            <Row
                                key               = {ri}
                                index             = {ri}
                                cols              = {cols}
                                row               = {row}
                                colOffset         = {colOffset}
                                checked           = {checkedRows.includes(row)}
                                toggleRowChecked  = {toggleRowChecked}
                                RowEditElement    = {RowEditElement}
                                hardWidthDisabled = {hardWidthDisabled}
                                RowClassByRow     = {RowClassByRow}
                                SeparatorElement  = {SeparatorElement}
                                isLast            = {rows.length - 1 === ri}
                                wrapperRef        = {wrapperRef}
                                closeEditOnScroll = {closeEditOnScroll}
                            />
                    )
                }
            </tbody>
}
;
